/**
 * AuthProvider — ClickPay login UI (frontend half of backend/auth/).
 *
 * Mounted at the app root (App.tsx). On boot it calls GET /api/auth/me
 * and resolves one of three modes:
 *
 *   'open'   — CLICKPAY_AUTH=off (the /me body carries auth_enabled:false).
 *              The app renders exactly as today: no login page, no user
 *              chip, every permission helper returns true.
 *   'authed' — 200 with an identity. User + permission flags stored.
 *   'login'  — 401 with auth_enabled:true. AuthGate (App.tsx) renders the
 *              Login page IN PLACE — the URL is untouched, so after a
 *              successful login the router lands on the originally
 *              requested route automatically.
 *
 * Fail-open on transport errors: if /me can't be reached (backend down)
 * we render the open app — same experience as today when the backend is
 * down (BackendStatus chip turns red, data calls fail visibly). With
 * enforcement on, the API middleware still blocks everything server-side,
 * so failing open here is a UX choice, not a security hole.
 *
 * Mid-session expiry: both API clients emit on any 401 response
 * (src/lib/authEvents.ts); the provider re-checks /me, which flips the
 * mode to 'login' when the session really is gone.
 */
import {
  createContext, useCallback, useContext, useEffect, useMemo, useRef,
  useState, type ReactNode,
} from 'react'
import { setSessionExpiredHandler } from './authEvents'
import { setSessionInfo } from './query/session'

/** Shape of GET /api/auth/me's 200 body (minus auth_enabled). */
export type AuthUser = {
  kind: 'user' | 'service' | string
  email: string | null
  name: string
  display_name: string | null
  is_admin: boolean
  can_write_ops: boolean
  can_write_ads: boolean
}

export type AuthMode = 'loading' | 'open' | 'authed' | 'login'

export type AuthContextValue = {
  mode: AuthMode
  /** Identity when mode==='authed'; null otherwise. */
  user: AuthUser | null
  /** POST /api/auth/login. Resolves on success (mode flips to 'authed');
   *  throws with a user-facing message on failure. `remember` (default true)
   *  → persistent session cookie; false → session-only cookie that the
   *  browser clears on close. */
  login: (email: string, password: string, remember?: boolean) => Promise<void>
  /** POST /api/auth/logout, then re-resolve /me (→ 'login' when auth is on). */
  logout: () => Promise<void>
  /** Re-run the /me check (e.g. after the Users admin edits yourself). */
  refresh: () => Promise<void>
  /** Permission helpers — true in 'open' mode (auth off = everything open,
   *  matching the backend middleware), else the identity's flag. */
  isAdmin: boolean
  canWriteOps: boolean
  canWriteAds: boolean
}

const AuthContext = createContext<AuthContextValue | null>(null)

type MeResolution = { mode: AuthMode; user: AuthUser | null }

/** GET /api/auth/me → mode + user. Never throws. */
async function resolveMe(): Promise<MeResolution> {
  let res: Response
  try {
    res = await fetch('/api/auth/me', {
      headers: { Accept: 'application/json' },
    })
  } catch {
    // Backend unreachable — fail open (see module docstring).
    return { mode: 'open', user: null }
  }
  let body: Record<string, unknown> = {}
  try {
    body = await res.json()
  } catch {
    /* non-JSON (proxy error page etc.) — treated below */
  }
  if (res.ok) {
    // auth_enabled:false + identity can happen when a session cookie from a
    // previous auth-on period is still valid: the spec for open mode is
    // "render exactly as today", so identity is ignored until the flip.
    if (body.auth_enabled === false) return { mode: 'open', user: null }
    return { mode: 'authed', user: body as unknown as AuthUser }
  }
  if (res.status === 401) {
    return body.auth_enabled === false
      ? { mode: 'open', user: null }
      : { mode: 'login', user: null }
  }
  // 5xx / unexpected — fail open so a backend hiccup can't lock the UI.
  return { mode: 'open', user: null }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [mode, setMode] = useState<AuthMode>('loading')
  const [user, setUser] = useState<AuthUser | null>(null)
  // Serialize concurrent refreshes (boot + a burst of 401 emits).
  const inflight = useRef<Promise<void> | null>(null)

  const refresh = useCallback(async () => {
    if (!inflight.current) {
      inflight.current = resolveMe()
        .then(r => { setMode(r.mode); setUser(r.user) })
        .finally(() => { inflight.current = null })
    }
    return inflight.current
  }, [])

  useEffect(() => { void refresh() }, [refresh])

  // Mid-session 401s from either API client → re-check /me. Guarded to the
  // 'authed' state so e.g. a stray 401 during the login flow can't loop.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      if (inflight.current) return
      void refresh()
    })
    return () => setSessionExpiredHandler(null)
  }, [refresh])

  const login = useCallback(async (email: string, password: string, remember = true) => {
    let res: Response
    try {
      res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, remember }),
      })
    } catch {
      throw new Error('Could not reach the server. Is the backend running?')
    }
    if (res.status === 401) {
      // Backend intentionally returns the same body for unknown user /
      // wrong password / disabled account (no enumeration).
      throw new Error('Invalid email or password.')
    }
    if (!res.ok) {
      throw new Error(`Login failed (${res.status}). Try again.`)
    }
    // Re-resolve /me for the canonical identity shape (kind/name fields)
    // instead of duplicating the login-response mapping here.
    await refresh()
  }, [refresh])

  const logout = useCallback(async () => {
    try {
      await fetch('/api/auth/logout', { method: 'POST' })
    } catch {
      /* logout is idempotent server-side; fall through to re-check */
    }
    await refresh()
  }, [refresh])

  // UI overhaul PR-0: mirror the bits shared components need (ErrorState's
  // admin-only "Technical details") into a context-free store so they never
  // have to import this module. AuthProvider is the only writer.
  useEffect(() => {
    setSessionInfo({ isAdmin: mode === 'open' || (user?.is_admin ?? false), mode })
  }, [mode, user])

  const value = useMemo<AuthContextValue>(() => ({
    mode,
    user,
    login,
    logout,
    refresh,
    isAdmin: mode === 'open' || (user?.is_admin ?? false),
    canWriteOps: mode === 'open' || (user?.can_write_ops ?? false),
    canWriteAds: mode === 'open' || (user?.can_write_ads ?? false),
  }), [mode, user, login, logout, refresh])

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

/** Access the auth state. Must be used under <AuthProvider>. */
// eslint-disable-next-line react-refresh/only-export-components -- context hook lives with its provider by design (same pattern as OperatorSelector's useActiveOperator)
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (!ctx) throw new Error('useAuth must be used within <AuthProvider>')
  return ctx
}
